import { SearchIcon, ThumbDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ReactNode } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { resolveChannelIcon } from '@/components/bots/Integration/ChannelLogo';

import { CheckGlyph, delay, SectionHeading, Stage, tone } from './primitives';
import { useReveal } from './useReveal';

export default function AgentInsights() {
  const { t } = useLocale('chatbots');
  const { ref, state } = useReveal<HTMLDivElement>(0.15);

  return (
    <section id='insights' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
          <SectionHeading
            className='lg:col-span-7'
            title={t('See every conversation.')}
            tail={t('Fix an answer once, and it stays fixed.')}
          />
          <p className={clsxm('text-lg leading-8 lg:col-span-5', tone.body)}>
            {t(
              'Read transcripts from every channel, spot the questions your agent found hard and teach it the right answer in a click. Leads, sentiment and a daily summary come straight to you.',
            )}
          </p>
        </div>

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-16')}
        >
          <Stage
            variant='indigo'
            halftoneAt='0% 100%'
            className='px-3 pb-3 pt-6 sm:px-10 sm:pb-12 sm:pt-12'
          >
            <p className='sr-only'>
              {t(
                'Example: the conversations inbox with chats from several channels, and an answer being corrected and saved to the agent’s knowledge.',
              )}
            </p>
            <div aria-hidden='true' className='relative'>
              <div className={styles.rv} style={delay(0)}>
                <InboxWindow />
              </div>
              <div className='mt-4 grid gap-4 sm:grid-cols-2 lg:absolute lg:end-0 lg:top-1/2 lg:mt-0 lg:w-[17.5rem] lg:-translate-y-1/2 lg:grid-cols-1'>
                <div className={styles.rv} style={delay(700)}>
                  <LeadCard />
                </div>
                <div className={styles.rv} style={delay(850)}>
                  <SentimentCard />
                </div>
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </section>
  );
}

function InboxWindow() {
  const { t } = useLocale('chatbots');

  const rows: {
    channel: string;
    who: string;
    snippet: ReactNode;
    time: string;
    active?: boolean;
  }[] = [
    {
      channel: 'web',
      who: t('Visitor'),
      snippet: t('Do you allow dogs?'),
      time: '2m',
      active: true,
    },
    {
      channel: 'FaWhatsapp',
      who: 'Lucía F.',
      snippet: <span lang='es'>¿Hay parking para huéspedes?</span>,
      time: '8m',
    },
    {
      channel: 'FaPhone',
      who: '+1 (415) 555-0132',
      snippet: t('Phone call, 1 min 12 s'),
      time: '14m',
    },
    {
      channel: 'Gmail',
      who: 'Tom Becker',
      snippet: t('Re: invoice for my stay'),
      time: '1h',
    },
    {
      channel: 'FaFacebookMessenger',
      who: 'Aiko Tanaka',
      snippet: t('Can I book the spa as well?'),
      time: '3h',
    },
  ];

  return (
    <div
      className={clsxm(
        'overflow-hidden rounded-2xl shadow-[0_40px_100px_-40px_rgba(30,20,90,0.5)] lg:me-[19.5rem]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center gap-6 border-b border-black/[0.06] px-5 dark:border-white/[0.06]'>
        {[t('Conversations'), t('Leads'), t('Analytics')].map((tab, index) => (
          <span
            key={tab}
            className={clsxm(
              '-mb-px border-b-2 py-3.5 text-sm font-medium',
              index === 0
                ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                : clsxm('border-transparent', tone.quiet),
            )}
          >
            {tab}
          </span>
        ))}
      </div>

      <div className='grid md:grid-cols-[17rem_1fr]'>
        <div className='hidden border-e border-black/[0.06] md:block dark:border-white/[0.06]'>
          <div className='p-3'>
            <div
              className={clsxm(
                'flex items-center gap-2 rounded-lg bg-[#f5f5f8] px-3 py-2 text-xs dark:bg-white/[0.05]',
                tone.quiet,
              )}
            >
              <SearchIcon className='h-3.5 w-3.5' />
              {t('Search conversations')}
            </div>
          </div>
          <ul className='pb-2'>
            {rows.map(({ channel, who, snippet, time, active }) => (
              <li
                key={who}
                className={clsxm(
                  'mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5',
                  active && 'bg-primary-50 dark:bg-primary-400/10',
                )}
              >
                <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#f3f3f7] dark:bg-white'>
                  {channel === 'web' ? (
                    <span className='h-3.5 w-3.5 rounded-full bg-[#b64d27]' />
                  ) : (
                    resolveChannelIcon(channel, 'h-4 w-4')
                  )}
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='flex items-baseline justify-between gap-2'>
                    <span
                      className={clsxm(
                        'truncate text-[13px] font-semibold',
                        tone.ink,
                      )}
                    >
                      {who}
                    </span>
                    <span
                      className={clsxm('text-[11px] tabular-nums', tone.quiet)}
                    >
                      {time}
                    </span>
                  </span>
                  <span className={clsxm('block truncate text-xs', tone.body)}>
                    {snippet}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className='relative space-y-4 p-5 text-[14px] leading-relaxed sm:p-6'>
          <div className='flex justify-end'>
            <p
              className={clsxm(
                'max-w-[80%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2.5 dark:bg-white/[0.07]',
                tone.ink,
              )}
            >
              {t('Do you allow dogs?')}
            </p>
          </div>
          <div>
            <p className='max-w-[80%] rounded-2xl rounded-es-md bg-[#b64d27]/10 px-4 py-2.5 text-[#7a3317] line-through decoration-[#b64d27]/40 dark:bg-[#b64d27]/20 dark:text-[#f2b59b]'>
              {t('I’m not sure about pets. Please contact the hotel directly.')}
            </p>
            <p className='mt-2 flex items-center gap-1.5 text-xs font-medium text-[#c2413b] dark:text-[#ff8a85]'>
              <ThumbDownIcon className='h-3.5 w-3.5' />
              {t('Visitor disliked this answer')}
            </p>
          </div>

          <div
            className={clsxm(
              styles.rv,
              'rounded-2xl p-4 shadow-[0_20px_50px_-20px_rgba(30,20,90,0.35)] ring-1 ring-primary-600/25 dark:ring-primary-400/30',
              tone.surface,
            )}
            style={delay(350)}
          >
            <p className={clsxm('text-[13px] font-semibold', tone.ink)}>
              {t('Revise answer')}
            </p>
            <p
              className={clsxm(
                'mt-2 rounded-xl bg-[#f7f7fa] px-3.5 py-2.5 text-[13px] leading-relaxed dark:bg-white/[0.04]',
                tone.ink,
              )}
            >
              {t(
                'Yes! Dogs up to 20 kg stay free in our garden rooms. Just mention it when you book.',
              )}
            </p>
            <p className='mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs'>
              <span className='flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400'>
                <CheckGlyph className='h-3.5 w-3.5' />
                {t('Saved to knowledge')}
              </span>
              <span className={tone.quiet}>
                {t('Your agent answers this way from now on.')}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadCard() {
  const { t } = useLocale('chatbots');

  return (
    <div
      className={clsxm(
        'h-full rounded-2xl p-4 shadow-[0_24px_60px_-24px_rgba(30,20,90,0.45)]',
        tone.surface,
        tone.line,
      )}
    >
      <p className='flex items-center justify-between'>
        <span className={clsxm('text-[13px] font-semibold', tone.ink)}>
          {t('New lead')}
        </span>
        <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
          {t('Just now')}
        </span>
      </p>
      <dl className='mt-3 space-y-1.5 text-[13px]'>
        <div className='flex justify-between gap-3'>
          <dt className={tone.quiet}>{t('Name')}</dt>
          <dd className={clsxm('font-medium', tone.ink)}>Anna Kowalska</dd>
        </div>
        <div className='flex justify-between gap-3'>
          <dt className={tone.quiet}>{t('Email')}</dt>
          <dd className={clsxm('truncate font-medium', tone.ink)}>
            anna@example.com
          </dd>
        </div>
        <div className='flex justify-between gap-3'>
          <dt className={tone.quiet}>{t('Interest')}</dt>
          <dd className={clsxm('truncate font-medium', tone.ink)}>
            {t('Group booking for 12')}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function SentimentCard() {
  const { t } = useLocale('chatbots');
  const parts = [
    { label: t('Positive'), value: 82, color: 'bg-emerald-500' },
    { label: t('Neutral'), value: 14, color: 'bg-[#c9c9d6] dark:bg-white/30' },
    { label: t('Negative'), value: 4, color: 'bg-[#e5484d]' },
  ];

  return (
    <div
      className={clsxm(
        'h-full rounded-2xl p-4 shadow-[0_24px_60px_-24px_rgba(30,20,90,0.45)]',
        tone.surface,
        tone.line,
      )}
    >
      <p className='flex items-baseline justify-between'>
        <span className={clsxm('text-[13px] font-semibold', tone.ink)}>
          {t('Sentiment this week')}
        </span>
        <span className={clsxm('text-[11px] tabular-nums', tone.quiet)}>
          1,284 {t('chats')}
        </span>
      </p>
      <div className='mt-4 flex h-2.5 gap-1 overflow-hidden rounded-full'>
        {parts.map(({ label, value, color }) => (
          <span
            key={label}
            className={clsxm('h-full rounded-full', color)}
            style={{ width: `${value}%` }}
          />
        ))}
      </div>
      <ul className='mt-3 space-y-1 text-[13px]'>
        {parts.map(({ label, value, color }) => (
          <li key={label} className='flex items-center gap-2'>
            <span className={clsxm('h-2 w-2 rounded-full', color)} />
            <span className={clsxm('flex-1', tone.body)}>{label}</span>
            <span className={clsxm('tabular-nums font-medium', tone.ink)}>
              {value}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
